import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { firebaseAuth } from '@/lib/firebase';
import { useSignIn } from '../hooks/use-sign-in';
import { AuthLayout, GoogleLogo, Input } from '@react-apps/design-system';

export function SignInPage() {
  const {
    signIn,
    signInWithToken,
    signInWithGoogle,
    isLoading,
    pendingProvider,
    error,
    errorAction,
  } = useSignIn();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const location = useLocation();
  const navigate = useNavigate();
  /**
   * Recado de quem nos mandou para cá — "senha alterada, entre novamente"
   * (`useChangePassword`), ou o pedido de entrar com a temporária
   * (`ChangePasswordRequiredPage`). Viaja no `state` do router, nunca na URL.
   *
   * Lido UMA vez para o estado local e apagado do histórico em seguida: o
   * `location.state` mora no `history.state` e sobreviveria a um reload,
   * repetindo o aviso para sempre.
   */
  const [notice] = useState(() => {
    const state = location.state as { message?: string } | null;
    return state?.message ?? '';
  });

  useEffect(() => {
    if (notice) {
      void navigate(location.pathname, { replace: true, state: null });
    }
  }, [notice, navigate, location.pathname]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      if (user) {
        void user.getIdToken().then((token) => signInWithToken(token));
      }
    });
    return unsubscribe;
  }, [signInWithToken]);

  const handleSubmit = (e: { preventDefault(): void }) => {
    e.preventDefault();
    void signIn({ email, password });
  };

  const handleGoogleSignIn = useCallback(() => {
    void signInWithGoogle();
  }, [signInWithGoogle]);

  return (
    <AuthLayout
      title="Acesse sua conta"
      subtitle="Entre com seu e-mail e senha para continuar"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Recado da navegação — some assim que houver erro pra não
                exibir duas mensagens que se contradizem. */}
        {notice && !error && (
          <div
            role="status"
            className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300"
          >
            {notice}
          </div>
        )}

        {/* E-mail */}
        <Input
          id="email"
          label="E-mail"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="seu@email.com"
        />

        {/* Senha */}
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label
              htmlFor="password"
              className="text-(--color-text-primary) text-sm font-medium"
            >
              Senha
            </label>
            <Link
              to="/forgot-password"
              className="text-(--color-accent) text-xs hover:underline"
            >
              Esqueceu a senha?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
          >
            {error}
            {errorAction && (
              <a
                href={errorAction.href}
                className="mt-2 block font-semibold underline"
              >
                {errorAction.label}
              </a>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="bg-(--color-accent) w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? 'Entrando…' : 'Entrar'}
        </button>
      </form>

      {/* Divider */}
      <div className="my-6 flex items-center gap-3">
        <div className="border-(--color-border) flex-1 border-t" />
        <span className="text-(--color-text-secondary) text-xs font-medium uppercase tracking-widest">
          ou continue com
        </span>
        <div className="border-(--color-border) flex-1 border-t" />
      </div>

      {/* Microsoft fora até a configuração do provedor estar pronta
          (login-com-microsoft.md § Validação): o hook e o aviso de conta já
          existente seguem prontos, e voltar é repor o botão. */}
      <div className="space-y-3">
        <FederatedButton
          label="Google"
          icon={<GoogleLogo size="sm" />}
          pending={pendingProvider === 'google.com'}
          disabled={isLoading}
          onClick={handleGoogleSignIn}
        />
      </div>
    </AuthLayout>
  );
}

/** Botão de entrada federada; o do popup aberto diz que está esperando. */
function FederatedButton({
  label,
  icon,
  pending,
  disabled,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  pending: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-busy={pending}
      className="border-(--color-border) bg-(--color-bg-primary) text-(--color-text-primary) hover:bg-(--color-bg-secondary) flex w-full items-center justify-center gap-3 rounded-lg border px-4 py-2.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50"
    >
      {icon}
      {pending ? `Aguardando ${label}…` : `Entrar com ${label}`}
    </button>
  );
}
